/**
 * OAuth token persistence — localStorage layer
 *
 * Manages the three OAuth tokens independently of the Cognito SDK's own
 * localStorage keys (CognitoIdentityServiceProvider.*) so both can coexist
 * during the OAUTH_ENABLED=false rollback window.
 *
 * Key contract:
 *   oauth_access_token   — Access token (JWT, ~1hr expiry)
 *   oauth_id_token       — ID token (JWT, ~1hr expiry)
 *   oauth_refresh_token  — Refresh token (opaque, ~30 days)
 *
 * PKCE verifier is NOT stored here — it lives in sessionStorage (pkce.js).
 */

const ACCESS_TOKEN_KEY = 'oauth_access_token';
const ID_TOKEN_KEY = 'oauth_id_token';
const REFRESH_TOKEN_KEY = 'oauth_refresh_token';
// Tracks how the current tokens were obtained:
//   'sdk'  — auth-ui SDK auth (email/password or magic link) → token handoff via hash
//   'pkce' — Cognito Hosted UI authorization code + PKCE exchange
const TOKEN_SOURCE_KEY = 'oauth_token_source';

/**
 * Decode the payload segment of a JWT string.
 * Returns null (never throws) — used internally for expiry checks only.
 *
 * @param {string} jwt
 * @returns {object|null} Parsed payload or null on any decode failure
 */
function safeDecodeJwtPayload(jwt) {
  if (!jwt || typeof jwt !== 'string') {
    return null;
  }

  const segments = jwt.split('.');
  if (segments.length !== 3 || !segments[1]) {
    return null;
  }

  try {
    const base64 = segments[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const jsonStr = new TextDecoder('utf-8').decode(bytes);
    return JSON.parse(jsonStr);
  } catch {
    return null;
  }
}

/**
 * Persist all three OAuth tokens to localStorage.
 * Tokens are written atomically as individual keys.
 *
 * @param {object} tokens
 * @param {string} tokens.accessToken
 * @param {string} tokens.idToken
 * @param {string} tokens.refreshToken
 */
export function saveTokens({ accessToken, idToken, refreshToken }) {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(ID_TOKEN_KEY, idToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

/**
 * Retrieve all three OAuth tokens from localStorage.
 * Returns null if any token is missing — callers must treat null as "not authenticated".
 *
 * @returns {{ accessToken: string, idToken: string, refreshToken: string }|null}
 */
export function getTokens() {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  const idToken = localStorage.getItem(ID_TOKEN_KEY);
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

  if (!accessToken || !idToken || !refreshToken) {
    return null;
  }

  return { accessToken, idToken, refreshToken };
}

/**
 * Remove all three OAuth tokens and the source flag from localStorage.
 * Does not affect any other localStorage keys (e.g. Cognito SDK keys).
 */
export function clearTokens() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(ID_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(TOKEN_SOURCE_KEY);
}

/**
 * Remove only the access and ID tokens from localStorage, keeping the refresh
 * token intact. Used on page unload so short-lived tokens are not left sitting
 * in storage — on the next page load `retrieveCurrentUser()` will silently
 * obtain fresh tokens via the retained refresh token.
 */
export function clearAccessAndIdTokens() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(ID_TOKEN_KEY);
}

/**
 * Record how the current tokens were obtained.
 * Must be called when tokens are saved so refreshSession() can route correctly.
 *
 * @param {'sdk'|'pkce'} source
 *   'sdk'  — auth-ui did SDK auth (authenticateUser / CUSTOM_AUTH) and handed
 *            tokens back via URL hash. Refresh via Cognito InitiateAuth API.
 *   'pkce' — tokens came from the Cognito Hosted UI authorization_code flow.
 *            Refresh via POST /oauth2/token.
 */
export function setTokenSource(source) {
  localStorage.setItem(TOKEN_SOURCE_KEY, source);
}

/**
 * Retrieve the stored token source flag.
 *
 * @returns {'sdk'|'pkce'|null}
 */
export function getTokenSource() {
  return localStorage.getItem(TOKEN_SOURCE_KEY);
}

/**
 * Retrieve only the refresh token from localStorage.
 * Used when clearAccessAndIdTokens() has removed the access+id tokens (on
 * beforeunload) but the refresh token is still available for a silent refresh.
 *
 * @returns {string|null}
 */
export function getRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Check whether a non-expired access token exists in localStorage.
 *
 * "Valid" means: the token is present AND its JWT `exp` claim is strictly
 * in the future. Tokens expiring at exactly the current second are treated
 * as expired — they will be invalid by the time any downstream call uses them.
 *
 * @returns {boolean}
 */
export function hasValidTokens() {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (!accessToken) {
    return false;
  }

  const payload = safeDecodeJwtPayload(accessToken);
  if (!payload || typeof payload.exp !== 'number') {
    return false;
  }

  const nowEpoch = Math.floor(Date.now() / 1000);
  return payload.exp > nowEpoch;
}
